import { useEffect, useState } from "react";
import bacDark from "@/assets/logo-bookaclassic.png";
import bacLight from "@/assets/logo-bookaclassic-inverse.png";
import dsDark from "@/assets/logo-driveshare.png";
import dsLight from "@/assets/logo-driveshare-inverse.png";
import { usePlatform } from "@/hooks/usePlatform";

interface LogoProps {
  size?: "sm" | "md" | "lg";
  variant?: "dark" | "light" | "auto";
  className?: string;
}

const sizeMap = {
  sm: 52,
  md: 72,
  lg: 96,
};

// Intrinsic aspect ratios of the wordmarks. Used to set an explicit width so
// the rendered box always reserves the right space — even before the image
// has loaded or if it fails to load entirely (e.g. the broken-image fallback
// in the auth modal report, task #2698).
// BookAclassic: 1489 × 414; DRIVESHARE: 1453 × 575.
const ASPECT_RATIO = { bac: 1489 / 414, driveshare: 1453 / 575 } as const;

// Stable fallback URLs served from /public. The Vite-imported assets above are
// the primary source — they get content-hashed for proper cache busting when
// the artwork changes. The fallback below is a deploy-stable URL that will
// keep working even when a stale client chunk references a hashed asset URL
// that no longer exists after a redeploy (task #2698).
const BASE_URL = import.meta.env.BASE_URL || "/";
const FALLBACKS = {
  bac: {
    dark: `${BASE_URL}images/logo-bookaclassic.png`,
    light: `${BASE_URL}images/logo-bookaclassic-inverse.png`,
  },
  driveshare: {
    dark: `${BASE_URL}images/logo-driveshare.png`,
    light: `${BASE_URL}images/logo-driveshare-inverse.png`,
  },
} as const;

const PRIMARIES = {
  bac: { dark: bacDark, light: bacLight },
  driveshare: { dark: dsDark, light: dsLight },
} as const;

export function Logo({ size = "md", variant = "dark", className }: LogoProps) {
  // Task #8034 — brand-cutover aware: the wordmark follows the active
  // platform (flag > env > "bac") via PlatformProvider context.
  const config = usePlatform();
  const { platform } = config;
  const brand = platform === "driveshare" ? "driveshare" : "bac";
  const height = sizeMap[size];
  const width = Math.round(height * ASPECT_RATIO[brand]);
  const shade = variant === "light" ? "light" : "dark";
  const primary = PRIMARIES[brand][shade];
  const fallback = FALLBACKS[brand][shade];
  const [src, setSrc] = useState<string>(primary);

  // If the consumer switches variant/brand we need to re-attempt the primary URL.
  useEffect(() => {
    setSrc(primary);
  }, [primary]);

  return (
    // Self-contained inline-flex wordmark. Consumers that need to show/hide
    // this responsively (e.g. the Header) must do so on a WRAPPER element, not
    // by passing `hidden`/`md:hidden` here — an unscoped display utility on the
    // same element as `hidden` is unreliable (Tailwind emits `inline-flex`
    // after `hidden`, so it would never hide).
    <span className={`inline-flex items-center${className ? ` ${className}` : ""}`}>
      <img
        src={src}
        alt={config.fullName}
        width={width}
        height={height}
        onError={() => {
          if (src !== fallback) setSrc(fallback);
        }}
        style={{
          height: `${height}px`,
          width: `${width}px`,
          objectFit: "contain",
        }}
      />
    </span>
  );
}
